// src/pages/Register/Register.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, CheckCircle, Mail, Lock, User, UserCircle, GraduationCap, Presentation } from "lucide-react";
import "./Register.css";
import AuthLayout from "../../components/AuthLayout/AuthLayout";
import GoogleAuthButton from "../../components/GoogleAuthButton/GoogleAuthButton";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Validation utilities
const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validatePassword = (password) => {
  return {
    isValid: password.length >= 8,
    hasMinLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
};

const validateUsername = (username) => {
  return /^[a-zA-Z0-9_]{3,30}$/.test(username);
};

const Register = () => {
  const [form, setForm] = useState({
    name: "",
    surname: "",
    username: "",
    email: "",
    password: "",
    role: "student",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [usernameTaken, setUsernameTaken] = useState("");
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
    setSuccess("");
    setResendMessage("");
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    if (field === "username" && validateUsername(form.username)) checkUsername(form.username);
  };

  // Tell people a username is taken as soon as they leave the field, not after they fill in everything else
  const checkUsername = async (name) => {
    try {
      const res = await fetch(`${API_URL}/auth/username-available?username=${encodeURIComponent(name)}`);
      const data = await res.json();
      setUsernameTaken(data.available === false ? name.toLowerCase() : "");
    } catch {
      setUsernameTaken("");   // the server checks again on submit
    }
  };

  const getFieldError = (field) => {
    if (!touched[field]) return "";
    
    if (field === "name" && form.name && form.name.length < 2) {
      return "First name must be at least 2 characters";
    }
    if (field === "surname" && form.surname && form.surname.length < 2) {
      return "Last name must be at least 2 characters";
    }
    if (field === "username" && form.username && !validateUsername(form.username)) {
      return "Username must be 3 to 30 characters (letters, numbers, underscore only)";
    }
    if (field === "username" && form.username && usernameTaken === form.username.toLowerCase()) {
      return "That username is already taken. Please choose another.";
    }
    if (field === "email" && form.email && !validateEmail(form.email)) {
      return "Please enter a valid email address";
    }
    if (field === "password" && form.password && !validatePassword(form.password).isValid) {
      return "Password must be at least 8 characters";
    }
    return "";
  };

  const passwordValidation = validatePassword(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    if (form.name.length < 2) {
      setError("First name must be at least 2 characters");
      return;
    }
    if (form.surname.length < 2) {
      setError("Last name must be at least 2 characters");
      return;
    }
    if (!validateUsername(form.username)) {
      setError("Username must be at least 3 characters (letters, numbers, underscore only)");
      return;
    }
    if (!validateEmail(form.email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!passwordValidation.isValid) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setResendMessage("");

    try {
      const res = await fetch(`${API_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      
      if (!res.ok) {
        // Better error messages based on status code
        if (res.status === 400) {
          throw new Error(data.detail || "Invalid registration data");
        } else if (res.status === 409) {
          throw new Error("Email or username already exists");
        } else {
          throw new Error(data.detail || "Registration failed");
        }
      }

      // Display success message with ID information
      let successMsg = "Registration successful! Please check your inbox or spam folder to verify your email.";

      if (form.role === "student" && data.student_id) {
        successMsg += ` Your Student ID is: ${data.student_id}`;
      } else if (form.role === "teacher" && data.teacher_id) {
        successMsg += ` Your Teacher ID is: ${data.teacher_id}`;
      }

      setSuccess(successMsg);
      
      // Optionally redirect to login after successful registration
      // setTimeout(() => {
      //   navigate("/login");
      // }, 3000);
      
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError("Cannot connect to server. Please check your internet connection.");
      } else {
        setError(err.message);
      }
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!form.email) {
      setResendMessage("Please enter your email first.");
      return;
    }

    if (!validateEmail(form.email)) {
      setResendMessage("Please enter a valid email address.");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Failed to resend verification");
      }

      setResendMessage("Verification email resent! Please check your inbox.");
    } catch (err) {
      setResendMessage(err.message);
    }
  };

  return (
    <AuthLayout heading="Start learning smarter today.">
    <div className="register-container">
      <div className="register-card">
        <h2>Create Account</h2>
        <p className="subtitle">Join us today and start your learning journey</p>

        {/* Error Banner */}
        {error && (
          <div className="error-banner">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Success Banner */}
        {success && (
          <div className="success-banner">
            <CheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        {/* Resend Message Banner */}
        {resendMessage && (
          <div className="info-banner">
            <Mail size={18} />
            <span>{resendMessage}</span>
          </div>
        )}

        <form className="register-form" onSubmit={handleSubmit}>
          {/* Name Fields Row */}
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input
                name="name"
                placeholder="John"
                value={form.name}
                onChange={handleChange}
                onBlur={() => handleBlur("name")}
                disabled={loading}
                required
                className={getFieldError("name") ? "input-error" : ""}
              />
              {getFieldError("name") && (
                <span className="field-error">{getFieldError("name")}</span>
              )}
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                name="surname"
                placeholder="Doe"
                value={form.surname}
                onChange={handleChange}
                onBlur={() => handleBlur("surname")}
                disabled={loading}
                required
                className={getFieldError("surname") ? "input-error" : ""}
              />
              {getFieldError("surname") && (
                <span className="field-error">{getFieldError("surname")}</span>
              )}
            </div>
          </div>

          {/* Username Field */}
          <div className="form-group">
            <label>Username</label>
            <div className="input-wrapper">
              <UserCircle size={20} className="input-icon" />
              <input
                name="username"
                placeholder="johndoe123"
                value={form.username}
                onChange={handleChange}
                onBlur={() => handleBlur("username")}
                disabled={loading}
                required
                className={getFieldError("username") ? "input-error with-icon" : "with-icon"}
              />
            </div>
            {getFieldError("username") && (
              <span className="field-error">{getFieldError("username")}</span>
            )}
          </div>

          {/* Email Field */}
          <div className="form-group">
            <label>Email Address</label>
            <div className="input-wrapper">
              <Mail size={20} className="input-icon" />
              <input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                onBlur={() => handleBlur("email")}
                disabled={loading}
                required
                autoComplete="email"
                className={getFieldError("email") ? "input-error with-icon" : "with-icon"}
              />
            </div>
            {getFieldError("email") && (
              <span className="field-error">{getFieldError("email")}</span>
            )}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label>Password</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Create a strong password"
                value={form.password}
                onChange={handleChange}
                onFocus={() => setShowPasswordStrength(true)}
                onBlur={() => {
                  handleBlur("password");
                  setTimeout(() => setShowPasswordStrength(false), 200);
                }}
                disabled={loading}
                required
                autoComplete="new-password"
                className={getFieldError("password") ? "input-error with-icon" : "with-icon"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                tabIndex="-1"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {getFieldError("password") && (
              <span className="field-error">{getFieldError("password")}</span>
            )}

            {/* Password Strength Indicator */}
            {showPasswordStrength && form.password && (
              <div className="password-strength">
                <div className="strength-item">
                  <span className={passwordValidation.hasMinLength ? "check-valid" : "check-invalid"}>
                    {passwordValidation.hasMinLength ? "✓" : "○"}
                  </span>
                  <span>At least 8 characters</span>
                </div>
                <div className="strength-item">
                  <span className={passwordValidation.hasUpperCase ? "check-valid" : "check-invalid"}>
                    {passwordValidation.hasUpperCase ? "✓" : "○"}
                  </span>
                  <span>One uppercase letter</span>
                </div>
                <div className="strength-item">
                  <span className={passwordValidation.hasLowerCase ? "check-valid" : "check-invalid"}>
                    {passwordValidation.hasLowerCase ? "✓" : "○"}
                  </span>
                  <span>One lowercase letter</span>
                </div>
                <div className="strength-item">
                  <span className={passwordValidation.hasNumber ? "check-valid" : "check-invalid"}>
                    {passwordValidation.hasNumber ? "✓" : "○"}
                  </span>
                  <span>One number</span>
                </div>
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div className="form-group">
            <label id="role-label">I am a...</label>
            <div className="role-picker" role="radiogroup" aria-labelledby="role-label">
              {[
                { value: "student", title: "Student", hint: "Learn & practice", Icon: GraduationCap },
                { value: "teacher", title: "Teacher", hint: "Plan & track", Icon: Presentation },
              ].map(({ value, title, hint, Icon }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={form.role === value}
                  className={`role-option ${form.role === value ? "selected" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, role: value }))}
                  disabled={loading}
                >
                  <span className="role-option-icon"><Icon size={20} /></span>
                  <span>
                    <strong>{title}</strong>
                    <small>{hint}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={
              loading ||
              !form.name ||
              !form.surname ||
              !form.username ||
              !form.email ||
              !form.password
            }
            className={loading ? "loading" : ""}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <GoogleAuthButton role={form.role} onError={setError} />

        {/* Resend Verification Section */}
        {success && (
          <div className="resend-section">
            <p>Didn't receive the email?</p>
            <button onClick={handleResendVerification} className="resend-button">
              Resend Verification Email
            </button>
          </div>
        )}

        {/* Login Link */}
        <div className="form-footer">
          <p className="login-link">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
    </AuthLayout>
  );
};

export default Register;